// tslint:disable: no-empty
import React, { Context, createContext, PropsWithChildren, useContext, useState, useEffect } from 'react'
import { identifyHotjar } from '../../utils/hotjar'
import { getToken, setApiToken } from '../api'
import {
	IAuth,
	TLoginRequest,
	TAuthActions,
	TRegisterRequest,
	IUser,
	TAccountRequest,
	TEeoRequest,
	TRequestResponse,
	TMemberOnboardingRequest,
} from '../types'
import useActions from './actions'
import QueryString from 'query-string'
import { useHistory } from 'react-router-dom'
import { customLocalStorage, customSessionStorage } from '../customStorage'
import { TRequestWithCustom } from '../../utils/custom-fields'

/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-empty-function */

const initialAuthState: IAuth = {
	user: null,
	token: null,
	loading: false,
}

const AuthContext: Context<{
	auth: IAuth
	actions: TAuthActions
}> = createContext({
	auth: {
		...initialAuthState,
		user: (JSON.parse(customLocalStorage.getItem('user') || 'null') as IUser) || null,
		token: customLocalStorage.getItem('token') || null,
	},
	actions: {
		login: (body: TLoginRequest) => {},
		googleLogin: (body: string) => {},
		register: (body: TRegisterRequest) => {},
		googleRegister: (body: string) => {},
		setError: () => {},
		logout: () => {},
		linkedinLogin: (body: string) => {},
		linkedinRegister: (body: string) => {},
		updateAccount: (accountData: TAccountRequest) => {},
		updateAdditionalInfo: (additionalData: TRequestWithCustom<TEeoRequest>) => {},
		updateEEO: (eeoData: TEeoRequest) => Promise.resolve({ ok: true } as TRequestResponse),
		updateOnboardingData: (onboardingData: Partial<TMemberOnboardingRequest>) =>
			Promise.resolve({ ok: true } as TRequestResponse),
		setUserFields: (user: Partial<IUser>) => {},
		cronofyFilled: (isFilled: boolean) => Promise.resolve({ ok: true } as TRequestResponse),
		refreshUser: () => {},
		resetPassword: (email: string) => Promise.resolve(),
		getCallEmail: () => Promise.resolve(),
	},
})

export const AuthProvider = (props: PropsWithChildren<{}>) => {
	const [loading, setLoading] = useState<boolean>(false)
	const [error, setProviderError] = useState<string | null>(null)
	const [callEmail, setCallEmail] = useState<string>(undefined)
	const [token, setToken] = useState<string | null>(customLocalStorage.getItem('token') || null)
	const [user, setUser] = useState<IUser | null>(
		(JSON.parse(customLocalStorage.getItem('user') || 'null') as IUser) || null
	)

	useEffect(() => {
		if (callEmail !== user?.callEmail) {
			setUser((prevUser) => ({
				...prevUser,
				callEmail,
			}))
		}
	}, [callEmail, user])

	const history = useHistory()
	const query: { [key: string]: any } = QueryString.parse(history.location.search, {
		arrayFormat: 'comma',
		parseNumbers: true,
	})
	const { reschedule_id } = query

	if (!token && reschedule_id) {
		customSessionStorage.setItem('redirectUrl', `${window.location.pathname}${window.location.search}`)
	}

	if (token && getToken()) {
		setApiToken(token)
	}

	useEffect(() => {
		if (user?.email) identifyHotjar(user.id, user.email)
	}, [user])

	// Invite codes routine
	const urlParams = new URLSearchParams(window.location.search)
	const inviteCode = urlParams.get('invitecode')
	const referrer = urlParams.get('referrer')
	const coupon = urlParams.get('coupon')

	if (inviteCode) {
		customLocalStorage.setItem('wiseInviteCode', inviteCode)
		customLocalStorage.removeItem('referrer')
	}

	if (coupon) {
		customLocalStorage.setItem('coupon', coupon)
	}

	const actions: TAuthActions = useActions(
		setLoading,
		setProviderError,
		setToken,
		setUser,
		setCallEmail,
		user,
		token,
		callEmail
	)

	return (
		<AuthContext.Provider value={{ auth: { loading, error, token, user }, actions }}>
			{props.children}
		</AuthContext.Provider>
	)
}

export const useAuthContext: () => { auth: IAuth; actions: TAuthActions } = () => {
	return useContext(AuthContext)
}
